import React, { useEffect, useMemo, useState } from 'react';
import { Wrench, Activity, Calendar, AlertTriangle, IndianRupee, Plus, CheckCircle2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPut } from '../lib/api';
import { toast } from 'sonner';
import { formatDate, formatINR, inrCompact } from '../lib/format';
import MachineDrawer from './equipment/MachineDrawer';
import MachineForm from './equipment/MachineForm';

const STATUS_COLOR = {
  Working:    'bg-emerald-100 text-emerald-700',
  Idle:       'bg-slate-100 text-slate-700',
  Down:       'bg-red-100 text-red-700',
  Maintenance:'bg-amber-100 text-amber-700',
};
const DOT = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' };

export default function Equipment() {
  const [fleet, setFleet] = useState({});
  const [dash, setDash] = useState(null);
  const [upcoming, setUpcoming] = useState({ upcoming: [], overdue_count: 0, total: 0 });
  const [due, setDue] = useState([]);
  const [tab, setTab] = useState('fleet');
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // { equipment, initialTab }
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [fl, up, dd, dl] = await Promise.all([
        apiGet('/equipment/utilisation/fleet-summary'),
        apiGet('/equipment/maintenance/upcoming?days=30'),
        apiGet('/equipment/fleet-dashboard'),
        apiGet('/equipment/maintenance-due'),
      ]);
      setFleet(fl); setUpcoming(up); setDash(dd); setDue(dl || []);
    } catch { toast.error('Load failed'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const fleetList = fleet.fleet || [];
  const overdueDue = due.filter(d => d.overdue_hours >= 0).length;

  return (
    <div>
      <PageHeader
        overline="Plant & Machinery"
        title="Equipment Fleet"
        subtitle="Depreciation · hour-meter service plans · fuel efficiency"
      />

      {/* Fleet dashboard KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="fleet-kpi-strip">
        <Stat label="Fleet Value" value={inrCompact(dash?.total_fleet_value)} icon={IndianRupee} />
        <Stat label="Book Value" value={inrCompact(dash?.current_book_value)} icon={IndianRupee} />
        <Stat label="Working / Idle" value={`${dash?.deployed ?? 0} / ${dash?.idle ?? 0}`} icon={Wrench} />
        <Stat label="Maintenance Due" value={dash?.maintenance_due_count ?? 0} icon={AlertTriangle}
          accent={(dash?.maintenance_due_count || 0) > 0 ? 'text-red-600' : ''} />
        <Stat label="Avg Utilisation" value={`${fleet.avg_utilisation_pct ?? 0}%`} icon={Activity}
          accent={(fleet.avg_utilisation_pct || 0) < 50 ? 'text-amber-600' : 'text-emerald-700'} />
      </div>

      <div className="border-b border-slate-200 mb-5 flex items-center gap-1">
        {[
          ['fleet', `Fleet Register · ${fleetList.length}`],
          ['maintenance', `Maintenance Due · ${due.length + upcoming.total}`],
        ].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`equipment-tab-${t}`}
            className={`px-4 py-2 text-sm font-display font-semibold border-b-2 -mb-px transition-colors ${tab === t ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
        {tab === 'fleet' && (
          <button onClick={() => setAddOpen(true)} data-testid="eq-add-btn"
            className="ml-auto mb-1 flex items-center gap-1 px-3 py-1.5 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-bold">
            <Plus size={12} /> Add Machine
          </button>
        )}
      </div>

      {tab === 'fleet' && (
        loading ? <Loading /> :
        fleetList.length === 0 ? <Empty msg="No equipment in fleet — add your first machine" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="fleet-register-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Equipment</th>
                <th className="px-4 py-2.5 text-left">Type</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-right">Book Value</th>
                <th className="px-4 py-2.5 text-right">Hour Meter</th>
                <th className="px-4 py-2.5 text-left">Location</th>
                <th className="px-4 py-2.5 text-center">Maint</th>
                <th className="px-4 py-2.5 text-right">Avg Util</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {fleetList.map(eq => (
                <tr key={eq.id} data-testid={`eq-row-${eq.id}`} onClick={() => setDrawer({ equipment: eq, initialTab: 'overview' })}
                  className="border-b border-slate-100 hover:bg-slate-50/40 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <div className="font-mono text-xs text-slate-700">{eq.eq_id}</div>
                    <div className="text-[11px] text-slate-500">{eq.make_model || eq.make || ''}</div>
                  </td>
                  <td className="px-4 py-2.5">{eq.type}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${STATUS_COLOR[eq.status] || STATUS_COLOR.Idle}`}>
                      {eq.status || 'Idle'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs">{eq.purchase_value ? formatINR(eq.book_value) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-xs font-display font-bold">{eq.current_hour_meter ?? 0} h</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{eq.location || '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${DOT[eq.maintenance_status] || DOT.green}`}
                      data-testid={`eq-maint-dot-${eq.id}`} title={`Maintenance: ${eq.maintenance_status || 'green'}`} />
                  </td>
                  <td className={`px-4 py-2.5 text-right tabular-nums font-display font-bold ${(eq.avg_utilisation_pct || 0) < 50 ? 'text-amber-600' : 'text-emerald-700'}`}>{eq.avg_utilisation_pct ?? 0}%</td>
                  <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setDrawer({ equipment: eq, initialTab: 'util' })} data-testid={`eq-log-util-${eq.id}`}
                        className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-emerald-500 hover:text-emerald-600 rounded-sm">LOG UTIL</button>
                      <button onClick={() => setDrawer({ equipment: eq, initialTab: 'maint' })} data-testid={`eq-schedule-maint-${eq.id}`}
                        className="px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] rounded-sm">SCHEDULE</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'maintenance' && (
        <div className="space-y-6">
          <DueFeed due={due} loading={loading} onOpen={(eqId) => {
            const eq = fleetList.find(f => f.id === eqId);
            if (eq) setDrawer({ equipment: eq, initialTab: 'plan' });
          }} />
          <div>
            <div className="kpi-overline mb-2">Date-based schedule (next 30 days) · {upcoming.total}</div>
            <MaintenanceTable upcoming={upcoming} loading={loading} reload={load} />
          </div>
        </div>
      )}

      {drawer && (
        <MachineDrawer
          equipment={drawer.equipment}
          initialTab={drawer.initialTab}
          onClose={() => setDrawer(null)}
          onChanged={load}
        />
      )}
      {addOpen && (
        <MachineForm onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />
      )}
    </div>
  );
}

const Loading = () => <div className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">Loading…</div>;
const Empty = ({ msg }) => <div className="text-center py-12 border border-dashed border-slate-200 rounded-sm text-slate-400 text-xs uppercase tracking-widest font-display">{msg}</div>;

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3">
      {Icon && <Icon size={18} className="text-slate-400 shrink-0" />}
      <div className="min-w-0">
        <div className="kpi-overline mb-0.5 truncate">{label}</div>
        <div className={`font-display font-extrabold text-xl tabular-nums ${accent || 'text-[#0F172A]'}`}>{value}</div>
      </div>
    </div>
  );
}

function DueFeed({ due, loading, onOpen }) {
  if (loading) return <Loading />;
  return (
    <div>
      <div className="kpi-overline mb-2">Hour-meter service plan · {due.length} due</div>
      {due.length === 0 ? <Empty msg="No hour-meter services due — fleet is green" /> :
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="maintenance-due-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Equipment</th>
                <th className="px-4 py-2.5 text-left">Service</th>
                <th className="px-4 py-2.5 text-right">Due At</th>
                <th className="px-4 py-2.5 text-right">Current</th>
                <th className="px-4 py-2.5 text-right">Overdue By</th>
                <th className="px-4 py-2.5 text-left">Location</th>
              </tr>
            </thead>
            <tbody>
              {due.map(d => {
                const overdue = d.overdue_hours >= 0;
                return (
                  <tr key={`${d.equipment_id}-${d.plan_item_id}`} data-testid={`due-row-${d.eq_id}`}
                    onClick={() => onOpen(d.equipment_id)}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50/40 ${overdue ? 'bg-red-50/40' : 'bg-amber-50/30'}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs">{d.eq_id}</div>
                      <div className="text-[11px] text-slate-500">{d.type}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{d.service_name} <span className="text-slate-400">every {d.every_hours}h</span></td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-xs">{d.due_at_hours} h</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-xs font-display font-bold">{d.current_hours} h</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums text-xs font-display font-bold ${overdue ? 'text-red-600' : 'text-amber-600'}`}>
                      {overdue ? `${d.overdue_hours} h overdue` : `due in ${Math.abs(d.overdue_hours)} h`}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-600">{d.location || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      }
    </div>
  );
}

function MaintenanceTable({ upcoming, loading, reload }) {
  const today = new Date().toISOString().slice(0, 10);
  const complete = async (id) => {
    try {
      await apiPut(`/equipment/maintenance/${id}/complete`, { notes: 'Completed via UI' });
      toast.success('Marked complete');
      reload();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <Loading />;
  if (!upcoming.upcoming.length) return <Empty msg="No maintenance scheduled in the next 30 days" />;

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left">Scheduled</th>
            <th className="px-4 py-2.5 text-left">Equipment</th>
            <th className="px-4 py-2.5 text-left">Kind</th>
            <th className="px-4 py-2.5 text-left">Description</th>
            <th className="px-4 py-2.5 text-right">Cost (₹)</th>
            <th className="px-4 py-2.5 text-left">Status</th>
            <th className="px-4 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {upcoming.upcoming.map(m => {
            const overdue = m.scheduled_for && m.scheduled_for < today;
            return (
              <tr key={m.id} data-testid={`maint-row-${m.id}`} className={`border-b border-slate-100 hover:bg-slate-50/40 ${overdue ? 'bg-red-50/30' : ''}`}>
                <td className={`px-4 py-2.5 font-mono text-xs ${overdue ? 'text-red-600 font-bold' : 'text-slate-600'}`}>
                  {formatDate(m.scheduled_for)}
                  {overdue && <span className="ml-2 text-[10px] uppercase tracking-widest">overdue</span>}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs">{m.eq_id}</td>
                <td className="px-4 py-2.5">
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest bg-amber-100 text-amber-700">{m.kind}</span>
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-600">{m.description || '—'}</td>
                <td className="px-4 py-2.5 text-right tabular-nums text-xs">{m.cost ? Number(m.cost).toLocaleString('en-IN') : '—'}</td>
                <td className="px-4 py-2.5 text-xs">{m.status}</td>
                <td className="px-4 py-2.5 text-right">
                  <button onClick={() => complete(m.id)} data-testid={`maint-complete-${m.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold border border-slate-300 hover:border-emerald-500 hover:text-emerald-600 rounded-sm">
                    <CheckCircle2 size={11} /> COMPLETE
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
